import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Lock, ArrowRight, Sparkles, TrendingUp, Shield, Globe, Zap, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(phone, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#0a0f0d]">
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-gradient-to-br from-[#0d1f17] via-[#0f2b1e] to-[#06140d]">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, rgba(34,197,94,0.08) 1px, transparent 0)`, backgroundSize: '40px 40px' }}></div>
        <div className="absolute top-20 left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-[128px] animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-green-400/8 rounded-full blur-[100px]"></div>
        <div className="relative z-10 flex flex-col justify-center px-16 w-full">
          <div className="mb-10">
            <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Zap size={20} className="text-white" fill="white" />
              </div>
              <div><span className="text-white font-bold text-lg tracking-tight">Interlink</span></div>
            </div>
          </div>
          <div className="mb-4">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles size={14} className="text-emerald-400" />
              <span className="text-emerald-400 text-xs font-semibold tracking-wider uppercase">Connect Your Business</span>
            </div>
            <h1 className="text-6xl font-black text-white leading-[1.1] mb-4">Grow your<br /><span className="bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">business</span><br />smartly.</h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-md">Real-time stock valuation, smart financial insights, and direct connection to investors — all in Swahili & English.</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-3"><TrendingUp size={20} className="text-emerald-400" /></div>
              <div className="text-2xl font-bold text-white">10K+</div><div className="text-xs text-gray-500 mt-1">Active Shops</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center mb-3"><Shield size={20} className="text-blue-400" /></div>
              <div className="text-2xl font-bold text-white">100%</div><div className="text-xs text-gray-500 mt-1">Secure Data</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mb-3"><Globe size={20} className="text-purple-400" /></div>
              <div className="text-2xl font-bold text-white">SW + EN</div><div className="text-xs text-gray-500 mt-1">Languages</div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-[#0a0f0d]">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center"><Zap size={20} className="text-white" fill="white" /></div>
              <div><span className="text-white font-bold text-lg">Interlink</span></div>
            </div>
          </div>
          <div className="bg-[#111a14] border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl shadow-black/50 backdrop-blur-sm">
            <div className="mb-8"><h2 className="text-3xl font-bold text-white">{t('login')}</h2></div>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-3">
                <div className="w-5 h-5 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-xs">!</span></div>{error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <div className={`relative transition-all duration-300 ${focused === 'phone' ? 'ring-2 ring-emerald-500/50 rounded-2xl' : ''}`}>
                  <div className="absolute left-4 top-3.5 text-emerald-400 font-semibold text-sm">+255</div>
                  <Phone size={18} className="absolute left-16 top-3.5 text-gray-500" />
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setPhone(digits);
                    }}
                    onFocus={() => setFocused('phone')}
                    onBlur={() => setFocused(null)}
                    placeholder="7XX XXX XXX"
                    className="w-full pl-28 pr-4 py-3.5 bg-[#0d1611] border border-white/10 rounded-2xl text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <div className={`relative transition-all duration-300 ${focused === 'password' ? 'ring-2 ring-emerald-500/50 rounded-2xl' : ''}`}>
                  <div className="absolute left-4 top-3.5 text-gray-500"><Lock size={18} className={focused === 'password' ? 'text-emerald-400' : ''} /></div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocused('password')} onBlur={() => setFocused(null)} placeholder={t('password')} className="w-full pl-12 pr-12 py-3.5 bg-[#0d1611] border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-3.5 text-gray-500 hover:text-gray-300 transition">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-emerald-500 to-green-500 text-white py-4 rounded-2xl font-semibold hover:from-emerald-600 hover:to-green-600 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 text-sm tracking-wide">
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <>{t('login')} <ArrowRight size={18} /></>}
              </button>
            </form>
            <div className="mt-6 text-center"><p className="text-gray-500 text-sm">New to Interlink?{' '}<Link to="/register" className="text-emerald-400 font-semibold hover:text-emerald-300 transition">Create free account</Link></p></div>
          </div>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-gray-600"><span className="hover:text-gray-400 cursor-pointer transition">Privacy</span><span className="w-1 h-1 bg-gray-700 rounded-full"></span><span className="hover:text-gray-400 cursor-pointer transition">Terms</span><span className="w-1 h-1 bg-gray-700 rounded-full"></span><span className="hover:text-gray-400 cursor-pointer transition">Help</span></div>
        </div>
      </div>
    </div>
  );
};

export default Login;
